import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Dialog, Grid, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import isEqual from "lodash/isEqual";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { DocumentsType, INewFile, Props, State } from "./interfaces";
import { DialogsTitle, TransactionScreen, Title, Actions } from "./styles";

import { CustomDate } from "../../../models/custom-date/custom-date";
import { DocumentTypeRef } from "../../../models/document-types";
import { DocumentDTO, DocumentRef } from "../../../models/documents";
import { ClientInvoiceRef } from "../../../models/legal-entity-clients/client-invoices";
import { ClientInvoiceDTO } from "../../../models/legal-entity-clients/client-invoices/client-invoice-dto";
import { VendorInvoiceDTO, VendorInvoiceRef } from "../../../models/legal-entity-vendors/vendor-invoices";
import { TransactionDTO, TransactionSTO } from "../../../models/transactions";

import AddEditButton from "../../../reusable-components/AddEditButton/AddEditButton";
import { loadDocumentType } from "../../../services/document-types";
import { loadAllDocuments, saveDocument, saveDocuments } from "../../../services/documents";
import { TransactionTypesApiService } from "../../../services/transaction-types/transaction-types-api.service";
import { saveTransaction } from "../../../services/transactions/transactions-api.service";
import { TransactionsApiService } from "../../../services/transactions/transactions-api.service";
import { saveTransactionDocument } from "../../../services/transactions-documents/transactions-documents-api.service";
import { TRANSACTION_STATUS_TYPES, TRANSACTION_TYPES } from "../../../utils/constants/constants";
import { areDatesTheSame, convertDateToUTC } from "../../../utils/time-utils";
import ConfirmationChangesDialog from "../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import { handleUploadNewFiles } from "../../Document/Document/utils";
import { saveLinkEntity } from "../../LinkEntity/utils";
import LoadingOverlay from "../../LoadingOverlay/LoadingOverlay";
import TransactionContext from "../transaction-context/transaction-context";
import TransactionScreenOne from "../TransactionScreenOne/TransactionScreenOne";
import TransactionScreenTwo from "../TransactionScreenTwo/TransactionScreenTwo";

import { setFieldByTransactionType } from "../utils";

const AddEditTransaction = ({
  effect = "add",
  buttonVariant = "plusButton",
  document,
  handleNotifyParent,
  legalEntity,
  date,
  item,
  invoice,
  loading = false,
  typeOfTransactions = "transaction",
  returnTransaction = false,
  handleClose,
  disabled = false,
  showOnlyFailedMessageOnSave = false,
  client,
  vendor,
  children,
  defaultProps,
  customSaveLogic,
  customDisabledFields,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const DEFAULT_TRANSACTION: Partial<TransactionDTO> = useMemo(() => {
    const dateInUtc =
      invoice?.issueDate ??
      (date ? new Date(date) : null) ??
      (item ? CustomDate.ToDate(item!.transactionDate) : new Date());

    return {
      comment: item?.comment ?? "",
      transactionDate: CustomDate.FromDate(dateInUtc),
      amount: item?.amount ?? 0,
      bookedAmount: item?.bookedAmount ?? 0,
      currency:
        item?.currency ??
        legalEntity?.defaultCurrency ??
        document?.legalEntity?.defaultCurrency ??
        client?.currency ??
        vendor?.currency ??
        null,
      type: item?.type ?? null,
      status: item?.status ?? {
        isDeleted: false,
        ...TRANSACTION_STATUS_TYPES.PENDING,
      },
      legalEntity:
        legalEntity ?? item?.legalEntity ?? document?.legalEntity ?? client?.legalEntity ?? vendor?.legalEntity ?? null,
      legislation: item?.legislation ?? null,
      clarifications: item?.clarifications ?? [],
      transactionDocuments: item?.transactionDocuments ?? [],
      dynamicFields: item?.dynamicFields ?? [],
      assets: item?.assets ?? [],
      client: client ?? item?.client ?? null,
      clientInvoice:
        typeOfTransactions === "clientInvoice" && !item
          ? (invoice as ClientInvoiceDTO) ?? null
          : item?.clientInvoice ?? null,
      vendor: vendor ?? item?.vendor ?? null,
      vendorInvoice:
        typeOfTransactions === "vendorInvoice" && !item
          ? (invoice as VendorInvoiceDTO) ?? null
          : item?.vendorInvoice ?? null,
      fromWhichBankAccount: item?.fromWhichBankAccount ?? null,
      toWhichBankAccount: item?.toWhichBankAccount ?? null,
      fromWhichCashAccount: item?.fromWhichCashAccount ?? null,
      toWhichCashAccount: item?.toWhichCashAccount ?? null,
      expenses: item?.expenses ?? [],
      revenues: item?.revenues ?? [],
      attributionsOfTheReceivedDonation: item?.attributionsOfTheReceivedDonation ?? [],
      loans: item?.loans ?? [],
      donatorMember: item?.donatorMember ?? null,
      donationBeneficiary: item?.donationBeneficiary ?? null,
      usagesOfDonationFunds: item?.usagesOfDonationFunds ?? [],
      ...(defaultProps ? defaultProps : {}),
    };
  }, [legalEntity, item, document, date, invoice, typeOfTransactions, client, vendor, defaultProps]);

  const INITIAL_STATE: State = useMemo(() => {
    return {
      isDialogOpen: false,
      loading: false,
      isCCDOpen: false,
      transaction: item ?? (DEFAULT_TRANSACTION as TransactionDTO),
      areFieldsValidated: true,
      documents: { document: null, newFiles: [] },
      documentToLink: null,
      defaultTransaction: null,
    };
  }, [effect, DEFAULT_TRANSACTION, item]);

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleDiscardConfirmationChangesDialog = useCallback(() => {
    setState({ ...INITIAL_STATE });
    handleClose && handleClose();
  }, [INITIAL_STATE]);

  const resetTransactionAndCloseDialog = (): void => {
    if (
      state.transaction &&
      state.transaction.clientInvoice &&
      !state.transaction.clientInvoice.client?.id &&
      !state.transaction.clientInvoice.invoiceNumber
    ) {
      state.transaction = { ...state.transaction, clientInvoice: null };
    }

    if (
      state.transaction &&
      state.transaction.vendorInvoice &&
      !state.transaction.vendorInvoice.vendor?.id &&
      !state.transaction.vendorInvoice.invoiceNumber
    ) {
      state.transaction = { ...state.transaction, vendorInvoice: null };
    }

    if (!isEqual(state.transaction, INITIAL_STATE.transaction)) {
      setState((state) => ({
        ...state,
        isCCDOpen: true,
      }));
    } else {
      handleDiscardConfirmationChangesDialog();
      handleClose && handleClose();
    }
  };

  const handleChangeField = (field: string, value: any): void => {
    setState((state) => ({
      ...state,
      transaction: {
        ...state.transaction,
        ...(field === "clientInvoice" && item
          ? {
              [field]: {
                ...value,
              },
            }
          : field === "vendorInvoice" && item
          ? {
              [field]: {
                ...value,
              },
            }
          : field === "legalEntity"
          ? {
              [field]: value,
              currency: value?.defaultCurrency,
              legislation: value?.legislation,
            }
          : field === "type"
          ? value === null
            ? {
                ...DEFAULT_TRANSACTION,
                legalEntity: state.transaction?.legalEntity,
                amount: state.transaction.amount,
                currency: state.transaction.currency,
                comment: state.transaction.comment,
              }
            : {
                [field]: value,
                ...(setFieldByTransactionType(value)?.clientInvoice
                  ? {
                      ...setFieldByTransactionType(value),
                      clientInvoice: {
                        ...(setFieldByTransactionType(value)?.clientInvoice as ClientInvoiceRef),
                        id: uuidv4(),
                      },
                    }
                  : setFieldByTransactionType(value)?.vendorInvoice
                  ? {
                      ...setFieldByTransactionType(value),
                      vendorInvoice: {
                        ...(setFieldByTransactionType(value)?.vendorInvoice as VendorInvoiceRef),
                        id: uuidv4(),
                      },
                    }
                  : {}),
              }
          : { [field]: value }),
      },
      documents: {
        document: field === "type" && !value ? null : state.documents.document,
        newFiles: field === "type" && !value ? [] : state.documents.newFiles,
      },
      documentToLink: field === "type" && !value ? null : state.documentToLink,
    }));
  };

  const handleSaveDocument = async (
    transaction: TransactionDTO,
    document: DocumentDTO,
    newFiles: INewFile[],
    correlationID?: string | null
  ) => {
    const response = await saveDocument(document, correlationID);

    const invokerId = response.invokerId;
    const correlationId = response.correlationId;

    await handleUploadNewFiles(document, newFiles, invokerId, correlationId);

    await saveTransactionDocument(transaction, document, correlationId);
  };

  const handleChangeDocumentCallback = (documents: DocumentsType): void => {
    setState((state) => ({
      ...state,
      documents: {
        ...documents,
        document: documents.document,
        newFiles: documents.newFiles,
      },
    }));
  };

  const handleSaveLinkItem = async (transaction: TransactionDTO, documentToLink: DocumentRef) => {
    const entityToLinkName = "document";
    const linkedEntityName = "transaction";

    await saveLinkEntity(linkedEntityName, transaction, entityToLinkName, documentToLink);
  };

  const handleChangeDocumentToLinkCallback = (documentToLink: DocumentDTO | null): void => {
    setState((state) => ({
      ...state,
      documentToLink,
    }));
  };

  const handleSaveTransaction = useCallback(
    async (transaction: TransactionDTO, documents: DocumentsType, documentToLink: DocumentRef) => {
      setState((state) => ({ ...state, loading: true }));

      const transactionId = uuidv4();

      let transactionToSave = { ...transaction };

      if (
        transactionToSave.clientInvoice &&
        !transactionToSave.clientInvoice.client?.id &&
        !transactionToSave.clientInvoice.invoiceNumber
      ) {
        transactionToSave = { ...transactionToSave, clientInvoice: null };
      }

      if (
        transactionToSave.vendorInvoice &&
        !transactionToSave.vendorInvoice.vendor?.id &&
        !transactionToSave.vendorInvoice.invoiceNumber &&
        !transactionToSave.vendorInvoice.vatCountry
      ) {
        transactionToSave = { ...transactionToSave, vendorInvoice: null };
      }

      let allDocumentsData: DocumentDTO[] = [];

      if (
        effect === "edit" &&
        transaction.type?.id === TRANSACTION_TYPES.ISSUED_CLIENT_INVOICE_ID &&
        item &&
        !!DEFAULT_TRANSACTION?.transactionDocuments?.length &&
        transaction.client?.id !== DEFAULT_TRANSACTION?.client?.id
      ) {
        const { items } = await loadAllDocuments(
          DEFAULT_TRANSACTION.transactionDocuments.map((doc) => doc.document?.id as string)
        );
        allDocumentsData = items.map((doc) => {
          return {
            ...doc,
            legalEntity: transaction.legalEntity,
            client: transaction.client,
            documentDate: CustomDate.ToDate(transaction.transactionDate) as Date,
          };
        });
      } else if (
        effect === "edit" &&
        transaction.type?.id === TRANSACTION_TYPES.RECEIVED_VENDOR_INVOICE_ID &&
        item &&
        !!DEFAULT_TRANSACTION?.transactionDocuments?.length &&
        transaction.vendor?.id !== DEFAULT_TRANSACTION.vendor?.id
      ) {
        const { items } = await loadAllDocuments(
          DEFAULT_TRANSACTION.transactionDocuments.map((doc) => doc.document?.id as string)
        );
        allDocumentsData = items.map((doc) => {
          return {
            ...doc,
            legalEntity: transaction.legalEntity,
            vendor: transaction.vendor,
            documentDate: CustomDate.ToDate(transaction.transactionDate) as Date,
          };
        });
      } else if (
        effect === "edit" &&
        item &&
        !!item.transactionDocuments?.length &&
        (transaction.legalEntity?.id !== item.legalEntity?.id ||
          !areDatesTheSame(
            CustomDate.ToDate(transaction.transactionDate) as Date,
            CustomDate.ToDate(item.transactionDate) as Date
          ))
      ) {
        const { items } = await loadAllDocuments(item.transactionDocuments.map((doc) => doc.document?.id as string));
        allDocumentsData = items.map((doc) => {
          return {
            ...doc,
            legalEntity: transaction.legalEntity,
            documentDate: CustomDate.ToDate(transaction.transactionDate) as Date,
          };
        });
      } else if (
        effect === "edit" &&
        item &&
        !!state.defaultTransaction?.transactionDocuments?.length &&
        (transaction.legalEntity?.id !== state.defaultTransaction?.legalEntity?.id ||
          !areDatesTheSame(
            CustomDate.ToDate(transaction.transactionDate) as Date,
            CustomDate.ToDate(state.defaultTransaction.transactionDate) as Date
          ))
      ) {
        const { items } = await loadAllDocuments(
          state.defaultTransaction.transactionDocuments.map((doc) => doc.document?.id as string)
        );
        allDocumentsData = items.map((doc) => {
          return {
            ...doc,
            legalEntity: transaction.legalEntity,
            documentDate: CustomDate.ToDate(transaction.transactionDate) as Date,
          };
        });
      }

      try {
        const { items } = await loadDocumentType(transaction?.type?.id);

        const { correlationId } = await saveTransaction(
          {
            ...transaction,
            ...(transaction.clientInvoice && {
              clientInvoice: {
                ...transaction.clientInvoice,
                issueDate: convertDateToUTC(transaction.clientInvoice.issueDate),
                dueDate: convertDateToUTC(transaction.clientInvoice.dueDate),
              },
            }),
            ...(transaction.vendorInvoice && {
              vendorInvoice: {
                ...transaction.vendorInvoice,
                issueDate: convertDateToUTC(transaction.vendorInvoice.issueDate),
                dueDate: convertDateToUTC(transaction.vendorInvoice.dueDate),
              },
            }),
            id: transaction?.id ? transaction.id : transactionId,
            transactionDate: transaction.transactionDate,
          } as TransactionSTO,
          showOnlyFailedMessageOnSave
        );

        if (allDocumentsData.length) {
          await saveDocuments(allDocumentsData, correlationId);
        }

        if (documents.document) {
          await handleSaveDocument(
            {
              ...transaction,
              id: transaction?.id ? transaction.id : transactionId,
            },
            {
              ...documents.document,
              legalEntity: transaction.legalEntity,
              type: items[0] as DocumentTypeRef,
              category: items[0]?.category,
              client: items[0]?.requireClient ? transaction.client : null,
              vendor: items[0]?.requireVendor ? transaction.vendor : null,
              documentDate: items[0]?.dateInsteadPeriod
                ? (CustomDate.ToDate(transaction.transactionDate) as Date)
                : documents.document.documentDate,
            },
            documents.newFiles,
            correlationId
          );
        }

        if (documentToLink) {
          await handleSaveLinkItem(
            {
              ...transaction,
              id: transaction?.id ? transaction.id : transactionId,
            },
            documentToLink
          );
        }

        if (document) {
          await saveTransactionDocument(
            {
              ...transaction,
              id: transaction?.id ? transaction.id : transactionId,
            },
            document
          );
        }

        effect === "add" && setState({ ...INITIAL_STATE });
        effect === "edit" &&
          setState((state) => ({
            ...state,
            isDialogOpen: false,
            loading: false,
            isCCDOpen: false,
            documents: { document: null, newFiles: [] },
            documentToLink: null,
          }));

        returnTransaction ? handleNotifyParent({ ...transaction, id: transactionId }) : handleNotifyParent();
      } catch (error: any) {
        setState((state) => ({
          ...state,
          loading: false,
          error: error?.message ?? error,
        }));
      }
    },
    [item, state, INITIAL_STATE, effect, handleNotifyParent, DEFAULT_TRANSACTION, state.defaultTransaction]
  );

  const loadItemCallback = useCallback(
    async (signal: AbortSignal) => {
      setState((state) => ({ ...state, loading: true }));
      if (item) {
        try {
          const response = await new TransactionsApiService().loadItems(
            {
              ids: [item.id],
            },
            undefined,
            [],
            undefined,
            undefined,
            60,
            signal
          );
          if (!response.error) {
            setState((state) => ({
              ...state,
              loading: false,
              transaction: response.items[0],
              defaultTransaction: response.items[0],
            }));
          }
        } catch (error: any) {
          setState((state) => ({
            ...state,
            loading: false,
            error: error?.message ?? error,
          }));
        }
      }
    },
    [item]
  );

  const loadTransactionType = useCallback(
    async (signal: AbortSignal, id: string) => {
      setState((state) => ({ ...state, loading: true }));
      if (id) {
        try {
          const response = await new TransactionTypesApiService().loadItems(
            {
              ids: [id],
            },
            undefined,
            [],
            undefined,
            undefined,
            60,
            signal
          );
          if (!response.error) {
            setState((state) => ({
              ...state,
              loading: false,
              transaction: {
                ...state.transaction,
                type: response.items[0],
              },
            }));
          }
        } catch (error: any) {
          setState((state) => ({
            ...state,
            loading: false,
            error: error?.message ?? error,
          }));
        }
      }
    },
    [handleChangeField, item, invoice]
  );

  useEffect(() => {
    const abortController = new AbortController();

    if (
      item &&
      Object.keys(item).includes("clarifications") //TODO : implement another logic to identify TransactionRef
    ) {
      setState((state) => ({
        ...state,
        transaction: item,
        defaultTransaction: item,
      }));
    } else if (item) {
      state.isDialogOpen && loadItemCallback(abortController.signal);
    }

    return () => abortController.abort();
  }, [item, state.isDialogOpen]);

  useEffect(() => {
    if (legalEntity && !item) {
      setState((state) => ({
        ...state,
        transaction: {
          ...state.transaction,
          legalEntity,
          currency: legalEntity.defaultCurrency,
        },
      }));
    }
  }, [legalEntity, item]);

  const isSaveButtonDisabled = useCallback(() => {
    return !(
      !!state.transaction.transactionDate &&
      !!state.transaction.type &&
      !!state.transaction.amount &&
      !!state.transaction.currency &&
      !!state.transaction.legalEntity &&
      !!state.transaction.comment
    );
  }, [state.transaction]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      areFieldsValidated: isSaveButtonDisabled(),
    }));
  }, [state.transaction, isSaveButtonDisabled]);

  useEffect(() => {
    setState((state) => ({
      ...state,
      loading,
    }));
  }, [loading]);

  useEffect(() => {
    const abortController = new AbortController();

    if (typeOfTransactions === "clientInvoice" && state.isDialogOpen) {
      loadTransactionType(abortController.signal, TRANSACTION_TYPES.ISSUED_CLIENT_INVOICE_ID);
      if (!item && invoice) {
        setState((state) => ({
          ...state,
          transaction: {
            ...state.transaction,
            comment: invoice?.comment,
            amount: invoice?.totalAmount,
            currency: invoice?.currency,
            transactionDate: CustomDate.FromDate(new Date(invoice?.issueDate)),
            client: invoice?.client,
          },
        }));
      }
    }
    if (typeOfTransactions === "vendorInvoice" && state.isDialogOpen) {
      loadTransactionType(abortController.signal, TRANSACTION_TYPES.RECEIVED_VENDOR_INVOICE_ID);
      if (!item && invoice) {
        setState((state) => ({
          ...state,
          transaction: {
            ...state.transaction,
            comment: invoice?.comment,
            amount: invoice?.totalAmount,
            currency: invoice?.currency,
            transactionDate: CustomDate.FromDate(new Date(invoice?.issueDate)),
            vendor: invoice?.vendor,
          },
        }));
      }
    }

    return () => abortController.abort();
  }, [typeOfTransactions, state.isDialogOpen, invoice, item]);

  useEffect(() => {
    if (defaultProps) {
      setState((state) => ({
        ...state,
        transaction: {
          ...state.transaction,
          defaultProps,
        },
      }));

      !!defaultProps.type && handleChangeField("type", defaultProps.type);
    }
  }, [defaultProps]);

  return (
    <TransactionContext.Provider value={{ typeOfTransactions, invoice }}>
      <Grid>
        {children ? (
          <Grid
            onClick={() => {
              setState((state) => ({ ...state, isDialogOpen: true }));
            }}
            title={effect === "add" ? "Add Transaction" : "Edit Transaction"}
          >
            {children}
          </Grid>
        ) : (
          <AddEditButton
            effect={effect}
            onClick={() => {
              setState((state) => ({ ...state, isDialogOpen: true }));
            }}
            title={effect === "add" ? "Add Transaction" : "Edit Transaction"}
            disabled={disabled}
            {...(buttonVariant === "text" &&
              (effect === "add" ? { buttonText: "Add Transaction" } : { buttonText: "Edit" }))}
          />
        )}

        <Dialog
          open={state.isDialogOpen}
          onClose={resetTransactionAndCloseDialog}
          fullWidth
          maxWidth={"lg"}
          PaperProps={{
            sx: {
              height: "100%",
            },
          }}
        >
          <LoadingOverlay loading={state.loading}>
            <Title>
              <DialogsTitle>
                <Typography>
                  {document
                    ? "Add a transaction linked to this document"
                    : item
                    ? `Edit transaction`
                    : `Add a transaction `}
                </Typography>
                <IconButton
                  onClick={resetTransactionAndCloseDialog}
                  style={{ color: theme.palette.error.main }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </IconButton>
              </DialogsTitle>
            </Title>
            <TransactionScreen>
              <TransactionScreenOne
                transaction={state.transaction}
                handleChangeField={handleChangeField}
                handleChangeDocument={handleChangeDocumentCallback}
                handleChangeDocumentToLink={handleChangeDocumentToLinkCallback}
                legalEntity={legalEntity}
                date={date}
                documents={state.documents}
                documentToLink={state.documentToLink}
                disableType={effect === "edit"}
                defaultTransaction={
                  item && effect === "edit"
                    ? ({
                        ...DEFAULT_TRANSACTION,
                        transactionDocuments:
                          state.defaultTransaction?.transactionDocuments ?? DEFAULT_TRANSACTION.transactionDocuments,
                      } as TransactionDTO)
                    : undefined
                }
                customDisabledFields={customDisabledFields}
              />
              <TransactionScreenTwo
                transaction={state.transaction}
                handleChangeField={handleChangeField}
                defaultTransaction={
                  item && effect === "edit"
                    ? ({
                        ...DEFAULT_TRANSACTION,
                        transactionDocuments:
                          state.defaultTransaction?.transactionDocuments ?? DEFAULT_TRANSACTION.transactionDocuments,
                      } as TransactionDTO)
                    : undefined
                }
              />
            </TransactionScreen>
            <Actions>
              <Grid>
                <Button
                  onClick={resetTransactionAndCloseDialog}
                  variant="outlined"
                  // color="secondary"
                  style={{
                    border: `1px solid ${theme.palette.actions.cancel}`,
                    color: theme.palette.actions.cancel,
                  }}
                >
                  {`${t(`buttons.cancel`)}`}
                </Button>
              </Grid>
              <Grid>
                <Button
                  onClick={() => {
                    if (customSaveLogic) {
                      customSaveLogic(state.transaction, state.documents, state.documentToLink as DocumentDTO);

                      setState((state) => ({ ...state, isDialogOpen: false }));
                    } else {
                      handleSaveTransaction(state.transaction, state.documents, state.documentToLink as DocumentDTO);
                    }
                  }}
                  disabled={state.areFieldsValidated}
                  variant="contained"
                >
                  {!date ? `${t(`buttons.save`)}` : `${t(`buttons.save_and_link`)}`}
                </Button>
              </Grid>
            </Actions>
          </LoadingOverlay>
        </Dialog>
        <ConfirmationChangesDialog
          open={state.isCCDOpen}
          handleCancel={() => setState({ ...state, isCCDOpen: false })}
          handleDiscard={handleDiscardConfirmationChangesDialog}
        />
      </Grid>
    </TransactionContext.Provider>
  );
};

export default AddEditTransaction;
